'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

interface UnitContextType {
  unitId: string | null;
  unitName: string | null;
  unitCodes: string[];
  unitQueryParams: string;
  loading: boolean;
  refreshUnit: () => Promise<void>;
}

const UnitContext = createContext<UnitContextType>({
  unitId: null,
  unitName: null,
  unitCodes: [],
  unitQueryParams: '',
  loading: true,
  refreshUnit: async () => {},
});

export const useUnit = () => useContext(UnitContext);

export const UnitProvider = ({ children }: { children: React.ReactNode }) => {
  const [unitId, setUnitId] = useState<string | null>(null);
  const [unitName, setUnitName] = useState<string | null>(null);
  const [unitCodes, setUnitCodes] = useState<string[]>([]);
  const [unitQueryParams, setUnitQueryParams] = useState<string>('');
  const [loading, setLoading] = useState(true);

  const fetchUnitInfo = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get('/api/unit-info');
      setUnitId(data.unitId || null);
      setUnitName(data.unitName || null);
      const codes: string[] = data.unitCodes && data.unitCodes.length > 0
        ? data.unitCodes
        : (data.unitId ? [data.unitId] : []);
      setUnitCodes(codes);
      const params = codes.length > 0
        ? codes.map((c: string) => `unit_ids=${encodeURIComponent(c)}`).join('&')
        : (data.unitId ? `unit_id=${encodeURIComponent(data.unitId)}` : '');
      setUnitQueryParams(params);
    } catch (error) {
      console.error('Failed to fetch unit info:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnitInfo();
  }, []);

  return (
    <UnitContext.Provider value={{ unitId, unitName, unitCodes, unitQueryParams, loading, refreshUnit: fetchUnitInfo }}>
      {children}
    </UnitContext.Provider>
  );
};

